import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { openExternal } from '../../../../src/utils/openExternal';
import { useFocusEffect, useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet } from '../../../../src/api/client';
import { config } from '../../../../src/config';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import ContentContainer from '../../../../src/components/ContentContainer';
import QrSvgView from '../../../../src/components/QrSvgView';
import { useToast } from '../../../../src/components/Toast';
import { useTheme } from '../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../src/utils/theme';
import { titleCase, formatPhone } from '../../../../src/utils/format';

interface LineItem {
  id: string;
  washType: string;
  retailPrice: number;
  createdAt: string;
  bayName: string;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
}

interface LocationGroup {
  locationId: string;
  locationName: string;
  taxJurisdiction: string | null;
  taxRate: number;
  groupSubtotal: number;
  groupDiscount: number;
  groupTax: number;
  groupTotal: number;
  lineItems: LineItem[];
}

interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  companyName: string;
  businessName: string;
  status: string;
  totalAmount: number;
  subtotal: number;
  discountAmount: number | null;
  discountPercentage: number | null;
  taxAmount: number;
  dueDate: string;
  paymentTermsDays: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  paidAt: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  groups: LocationGroup[];
  transactionCount: number;
  payUrl: string | null;
  payQrSvgDataUrl: string | null;
}

export default function UserFleetInvoiceDetailScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await apiGet<InvoiceDetail>(`/api/fleet/me/invoices/${id}`);
      setInvoice(data);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtTime = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const fmtPct = (rate: number) => `${(rate * 100).toFixed(3).replace(/\.?0+$/, '')}%`;

  if (loading && !invoice) {
    return <View style={styles.loadingContainer}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }
  if (!invoice) return null;

  const isUnpaid = invoice.status !== 'paid' && invoice.status !== 'cancelled';
  const htmlUrl = `${config.apiUrl}/api/fleet/me/invoices/${invoice.id}/html`;

  return (
    <View style={{ flex: 1 }}>
      <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: isUnpaid ? 96 : spacing.xxl }}>
        <ContentContainer>
          <Card style={styles.headerCard}>
            <Text style={styles.invoiceNumber}>#{invoice.invoiceNumber}</Text>
            <Text style={styles.business}>From {invoice.businessName}</Text>
            <Text style={styles.amount}>{fmt(invoice.totalAmount)}</Text>
            <Text style={styles.statusLine}>
              {invoice.status === 'paid'
                ? `Paid ${invoice.paidAt ? fmtDate(invoice.paidAt) : ''}${invoice.paymentMethod ? ` via ${invoice.paymentMethod}` : ''}`
                : `Due ${fmtDate(invoice.dueDate)}`}
            </Text>
          </Card>

          {invoice.groups.map(g => (
            <Card key={g.locationId} style={styles.groupCard}>
              <Text style={styles.groupHeading}>
                {g.locationName}{g.taxJurisdiction ? ` · ${g.taxJurisdiction}` : ''}
              </Text>
              <Text style={styles.groupSub}>Tax {fmtPct(g.taxRate)}</Text>
              {g.lineItems.map(li => (
                <View key={li.id} style={styles.lineItem}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.lineDesc}>{li.washType}{li.bayName ? ` — ${li.bayName}` : ''}</Text>
                    <Text style={styles.lineSub}>{fmtTime(li.createdAt)}</Text>
                    {(li.userName || li.userEmail || li.userPhone) ? (
                      <Text style={styles.lineWho}>{[li.userName ? titleCase(li.userName) : null, li.userEmail, li.userPhone ? formatPhone(li.userPhone) : null].filter(Boolean).join(' · ')}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.linePrice}>{fmt(li.retailPrice)}</Text>
                </View>
              ))}
              <View style={styles.groupTotals}>
                <View style={styles.totalRow}><Text style={styles.totalLabel}>Subtotal</Text><Text style={styles.totalValue}>{fmt(g.groupSubtotal)}</Text></View>
                {g.groupDiscount > 0 ? (
                  <View style={styles.totalRow}><Text style={[styles.totalLabel, { color: colors.success }]}>Discount</Text><Text style={[styles.totalValue, { color: colors.success }]}>-{fmt(g.groupDiscount)}</Text></View>
                ) : null}
                <View style={styles.totalRow}><Text style={styles.totalLabel}>Tax</Text><Text style={styles.totalValue}>{fmt(g.groupTax)}</Text></View>
                <View style={[styles.totalRow, styles.groupTotalRow]}>
                  <Text style={styles.groupTotalLabel}>Location Total</Text>
                  <Text style={styles.groupTotalValue}>{fmt(g.groupTotal)}</Text>
                </View>
              </View>
            </Card>
          ))}

          <Card style={styles.grandTotalCard}>
            <View style={styles.totalRow}><Text style={styles.totalLabel}>Subtotal (all locations)</Text><Text style={styles.totalValue}>{fmt(invoice.subtotal)}</Text></View>
            {(invoice.discountAmount ?? 0) > 0 ? (
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: colors.success }]}>Total Discount{invoice.discountPercentage ? ` (${invoice.discountPercentage}%)` : ''}</Text>
                <Text style={[styles.totalValue, { color: colors.success }]}>-{fmt(invoice.discountAmount ?? 0)}</Text>
              </View>
            ) : null}
            <View style={styles.totalRow}><Text style={styles.totalLabel}>Total Tax</Text><Text style={styles.totalValue}>{fmt(invoice.taxAmount)}</Text></View>
            <View style={[styles.totalRow, styles.grandTotalRow]}>
              <Text style={styles.grandTotalLabel}>Grand Total</Text>
              <Text style={styles.grandTotalValue}>{fmt(invoice.totalAmount)}</Text>
            </View>
          </Card>

          {invoice.status === 'paid' && invoice.paymentReference ? (
            <Card style={styles.receiptCard}>
              <Text style={styles.receiptTitle}>Payment Receipt</Text>
              {invoice.paidAt ? <Text style={styles.receiptLine}>Paid on {fmtDate(invoice.paidAt)}</Text> : null}
              {invoice.paymentMethod ? <Text style={styles.receiptLine}>Method: {invoice.paymentMethod}</Text> : null}
              <Text style={[styles.receiptLine, { fontSize: fontSize.xs, color: colors.textSecondary }]}>Ref: {invoice.paymentReference}</Text>
            </Card>
          ) : null}

          <TouchableOpacity
            style={styles.downloadBtn}
            onPress={() => openExternal(htmlUrl)}
          >
            <Ionicons name="download-outline" size={16} color={colors.primary} />
            <Text style={styles.downloadText}>Download Invoice</Text>
          </TouchableOpacity>

          {invoice.status === 'paid' ? (
            <View style={styles.paidBadge}>
              <Ionicons name="checkmark-circle" size={18} color={colors.success} />
              <Text style={styles.paidBadgeText}>Paid</Text>
            </View>
          ) : invoice.payQrSvgDataUrl ? (
            <Card style={styles.qrCard}>
              <Text style={styles.qrTitle}>Scan to Pay</Text>
              <Text style={styles.qrSub}>Anyone with a card can pay this invoice — no RinsePay account needed.</Text>
              <QrSvgView uri={invoice.payQrSvgDataUrl} width={200} fallbackRatio={1} accessibilityLabel="Invoice payment QR code" />
              {invoice.payUrl ? <Text style={styles.qrUrl}>{invoice.payUrl}</Text> : null}
            </Card>
          ) : null}

          <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>

      {isUnpaid ? (
        <View style={styles.payBar}>
          <Button
            title={`Pay Invoice — ${fmt(invoice.totalAmount)}`}
            onPress={() => router.push(`/user/fleet/invoices/${invoice.id}/pay` as never)}
            icon={<Ionicons name="card" size={18} color={colors.white} />}
          />
        </View>
      ) : null}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  headerCard: { alignItems: 'center', paddingVertical: spacing.xl, marginBottom: spacing.lg },
  invoiceNumber: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  business: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 4 },
  amount: { fontSize: fontSize.hero, fontWeight: fontWeight.bold, color: c.primary, marginTop: spacing.md },
  statusLine: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },

  groupCard: { marginBottom: spacing.md },
  groupHeading: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  groupSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2, marginBottom: spacing.sm },

  lineItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  lineDesc: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  lineSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  lineWho: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2, fontStyle: 'italic' },
  linePrice: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary },

  groupTotals: { paddingTop: spacing.sm, marginTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 },
  totalLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  totalValue: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  groupTotalRow: { marginTop: spacing.xs, paddingTop: spacing.xs, borderTopWidth: 1, borderTopColor: c.border },
  groupTotalLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text },
  groupTotalValue: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.primary },

  grandTotalCard: { marginBottom: spacing.md, padding: spacing.lg },
  grandTotalRow: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.border },
  grandTotalLabel: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  grandTotalValue: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primary },

  receiptCard: { marginBottom: spacing.md, padding: spacing.lg, borderLeftWidth: 4, borderLeftColor: c.success },
  receiptTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.success, marginBottom: spacing.sm },
  receiptLine: { fontSize: fontSize.sm, color: c.text, marginBottom: 2 },

  downloadBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, paddingVertical: spacing.md },
  downloadText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.primary },

  paidBadge: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, paddingVertical: spacing.md },
  paidBadgeText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.success },
  qrCard: { alignItems: 'center', padding: spacing.lg, marginBottom: spacing.md },
  qrTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  qrSub: { fontSize: fontSize.xs, color: c.textSecondary, textAlign: 'center', marginTop: 2, marginBottom: spacing.md, maxWidth: 280 },
  qrUrl: { fontSize: fontSize.xs, color: c.textSecondary, textAlign: 'center', marginTop: spacing.sm },

  payBar: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: spacing.md, backgroundColor: c.surface, borderTopWidth: 1, borderTopColor: c.border },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
